import { pointsForWord } from '../../game/rules';
import { scoreMultiplier } from '../../game/power-ups';
import type { ActiveEffect } from '../../shared/power-ups';
import { mightBeSolution, type DecodedSolutionFilter } from '../../shared/solution-filter';

export type LocalVerdict =
  /** The filter proves the word is not a solution; nothing is sent. */
  | { kind: 'notword' }
  /** A filter hit: present as accepted with these points while the server confirms. */
  | { kind: 'presumed'; points: number }
  /** No filter for this round; the server alone decides. */
  | { kind: 'unknown' };

/**
 * What the browser can say about a traced word before the server answers. Points mirror the
 * server's base scoring and timed multipliers; a pickup bonus, if any, arrives with the verdict.
 */
export function localVerdict(input: {
  word: string;
  filter: DecodedSolutionFilter | null;
  effects: readonly Readonly<ActiveEffect>[];
  now: number;
}): LocalVerdict {
  if (!input.filter) return { kind: 'unknown' };
  if (!mightBeSolution(input.filter, input.word)) return { kind: 'notword' };
  return {
    kind: 'presumed',
    points: pointsForWord(input.word) * scoreMultiplier({ effects: input.effects }, input.now),
  };
}

export type LocalSubmission =
  /** Play is closed; nothing to judge. */
  | { kind: 'closed' }
  | { kind: 'reject'; message: string; feedback: 'shake' | 'duplicate' }
  /** The same word is still awaiting the server; keep showing that entry. */
  | { kind: 'pending'; submissionId: string }
  | { kind: 'send'; presumedPoints: number | undefined };

/** What the board does with a traced word before any server round trip. */
export function localSubmission(input: {
  word: string;
  canPlay: boolean;
  minLength: number;
  found: ReadonlyMap<string, number>;
  pending: readonly { id: string; word: string; pending: boolean }[];
  filter: DecodedSolutionFilter | null;
  effects: readonly Readonly<ActiveEffect>[];
  now: number;
}): LocalSubmission {
  if (!input.canPlay) return { kind: 'closed' };
  if (input.word.length < input.minLength) return { kind: 'reject', message: 'too short', feedback: 'shake' };
  if (input.found.has(input.word))
    return { kind: 'reject', message: 'already found!', feedback: 'duplicate' };
  const waiting = input.pending.find((entry) => entry.pending && entry.word === input.word);
  if (waiting) return { kind: 'pending', submissionId: waiting.id };
  const verdict = localVerdict(input);
  if (verdict.kind === 'notword') return { kind: 'reject', message: 'not a word', feedback: 'shake' };
  return { kind: 'send', presumedPoints: verdict.kind === 'presumed' ? verdict.points : undefined };
}

/** Confirmed points plus presumed points of this round's words still awaiting the server. */
export function tentativeScore(
  found: ReadonlyMap<string, number>,
  submissions: readonly { round: string; pending: boolean; presumedPoints: number | undefined }[],
  roundId: string | null,
): number {
  let total = 0;
  for (const points of found.values()) total += points;
  for (const entry of submissions)
    if (entry.pending && entry.round === roundId) total += entry.presumedPoints ?? 0;
  return total;
}
